import React, { useState, useEffect } from 'react'
import { View, ScrollView, ActivityIndicator } from 'react-native'
import moment from 'moment'

import Timeline from 'react-calendar-timeline'
import 'react-calendar-timeline/lib/Timeline.css'
import { DateTime } from 'luxon'
import { useDispatch } from 'react-redux'

import MSFESHeading from './MSFESHeading'
import { getColor } from '../screens/CalendarViewScreenHelpers/CalendarEntityList'
import { useMonthLoadingHelper } from '../screens/CalendarViewScreenHelpers/useMonthLoadingHelper'
import { ViewEntityOverlay } from '../screens/CalendarViewScreenHelpers/ViewEntityOverlay'
import YesNoCancelDialog from './YesNoCancelDialog'
import { deleteCalendarEvent } from '../store/calendar/actions'
import { useLoadTableData } from '../hooks/useLoadTableData'

export const SchedulerComponent = ({ entityType, sourceKey, route }) => {
  const {
    data: personnel,
    meta: personnelMeta,
    isLoading: personnelIsLoading,
    fetchAnotherPage
  } = useLoadTableData({
    entityType: { name: 'personnel' },
    sourceKey: 'personnel'
  })

  useEffect(() => {
    if (personnelMeta?.current_page < personnelMeta?.last_page) {
      fetchAnotherPage()
    }
  }, [personnelMeta?.current_page])

  const {
    data,
    meta,
    updateFilter,
    isLoading: isLoadingShifts
  } = useLoadTableData({
    entityType,
    sourceKey,
    route,
    per_page: 192
  })

  const [viewingRange, setViewingRange] = useState(null)
  const [deletingEvent, setDeletingEvent] = useState(null)

  const [
    ,
    ,
    setActiveMonthYear,
    refetchMonth,
    byMonthYearThisEntityType
  ] = useMonthLoadingHelper(viewingRange?.startOfDay, entityType?.id)

  const dispatch = useDispatch()

  const deleteScheduleItemConfirmPressed = () => {
    dispatch(deleteCalendarEvent(deletingEvent))
      .then(() => {
        setIsViewingEntity(null)
        setDeletingEvent(false)
      })
      .catch(() => {})
      .finally(() => {
        setIsViewingEntity(null)
        setDeletingEvent(false)
      })
  }

  useEffect(() => {
    if (viewingRange?.endOfDay) {
      setActiveMonthYear({
        month: viewingRange?.endOfDay.split('-')[1],
        year: viewingRange?.endOfDay.split('-')[0]
      })
    }
  }, [viewingRange?.startOfDay])

  const groups = [
    ...(personnel
      ? personnel.map((person) => ({
          id: person.id,
          title: person.display_name
        }))
      : []),
    { id: 0, title: 'No assignee', stackItems: true, height: 60 }
  ]

  const [dataForScheduler, setDataForScheduler] = useState([])

  useEffect(() => {
    const _dataForScheduler = []

    const startProperty =
      entityType?.object_data?.start_datetime_field?.field_data?.property
    const endProperty =
      entityType?.object_data?.end_datetime_field?.field_data?.property

    data.forEach((item) => {
      const isAwaitingApproval =
        !item.assignee?.id && item.data_requiring_approval?.assignee?.id

      const color = getColor(item, entityType)

      // Get all assignees (assignee, assignee_2, assignee_3, etc.)
      const assignees = []
      item.assignee?.id && assignees.push(item.assignee)
      item.assignee_2?.id && assignees.push(item.assignee_2)
      item.assignee_3?.id && assignees.push(item.assignee_3)
      item.assignee_4?.id && assignees.push(item.assignee_4)
      item.assignee_5?.id && assignees.push(item.assignee_5)

      // If no assignees, add to "No assignee" group
      if (assignees.length === 0) {
        assignees.push({ id: 0 })
      }

      // Create a tile for each assignee
      assignees.forEach((assignee, index) => {
        _dataForScheduler.push({
          id: `${item.id}-${assignee.id}-${index}`, // Unique ID for each tile
          title: (isAwaitingApproval ? '*' : '') + item.display_name,
          group: assignee.id,
          item: item,
          start_time: DateTime.fromISO(
            item[startProperty]?.dateObject
          ).toMillis(),
          itemProps: {
            style: {
              background: color ?? 'blue',
              borderRadius: '4px',
              border: 0
            }
          },
          end_time: DateTime.fromISO(item[endProperty]?.dateObject).toMillis()
        })
      })
    })

    byMonthYearThisEntityType?.data?.virtualEvents?.forEach(
      (virtualEventDayOfMonth) => {
        virtualEventDayOfMonth.events.forEach((virtualEvent) => {
          const color = getColor(virtualEvent?.entity, entityType)

          const scheduleObject = {
            id: 'virtual_' + virtualEvent.entity?.virtual_event_signature,
            title: virtualEvent.entity?.display_name,
            item: virtualEvent.entity,
            group: virtualEvent.entity?.assignee?.id ?? 0,
            start_time: DateTime.fromISO(
              virtualEvent.entity?.[startProperty]?.dateObject
            ).toMillis(),
            end_time: DateTime.fromISO(
              virtualEvent.entity?.[endProperty]?.dateObject
            ).toMillis(),
            itemProps: {
              style: {
                background: color ?? 'blue',
                borderRadius: '4px',
                border: 0
              }
            }
          }
          _dataForScheduler.push(scheduleObject)
        })
      }
    )

    setDataForScheduler(_dataForScheduler)
  }, [meta, byMonthYearThisEntityType])

  const [isViewingEntity, setIsViewingEntity] = useState(null)

  return (
    <View style={{ flex: 1 }}>
      <View>
        <MSFESHeading>
          Viewing range: {viewingRange?.startOfDay} - {viewingRange?.endOfDay}{' '}
          {(isLoadingShifts || personnelIsLoading) && (
            <ActivityIndicator size="small" />
          )}
        </MSFESHeading>
      </View>

      {/* {isViewingEntity && (
        <MSFESNestedAddEntity
          entityType={entityType}
          tenant={isViewingEntity?.tenant_id}
          mode={viewMode}
          id={isViewingEntity?.id}
          onDismiss={onDismiss}
          shouldDismissOnSaved={true}
        />
      )} */}
      {!!isViewingEntity && (
        <ViewEntityOverlay
          entity={isViewingEntity}
          virtualEventCreateDate={DateTime.fromISO(
            viewingRange?.startOfDay
          ).toFormat('yyyy-MM-dd')}
          onDismiss={() => setIsViewingEntity(null)}
          onRefetch={() => refetchMonth()}
          onDeleteSchedulePressed={() => {
            setDeletingEvent(isViewingEntity.virtual_event_id)
          }}
        />
      )}
      {deletingEvent && (
        <YesNoCancelDialog
          title="Delete"
          body="Are you sure you want to delete this schedule item? This is a permanent action. No future entities will be created for this schedule."
          yesAction={deleteScheduleItemConfirmPressed}
          noAction={() => setDeletingEvent(null)}
          cancelAction={() => setDeletingEvent(null)}
        />
      )}
      <ScrollView style={{ flex: 1 }}>
        {groups?.length > 0 && (
          <Timeline
            minZoom={60 * 60 * 24 * 1000 * 1} // 1 day
            maxZoom={60 * 60 * 24 * 31 * 1000} /* 1 month */
            canMove={false}
            stackItems={true}
            canResize={false}
            groups={groups}
            itemRenderer={({
              item,
              itemContext,
              getItemProps,
              getResizeProps
            }) => {
              const {
                left: leftResizeProps,
                right: rightResizeProps
              } = getResizeProps()
              return (
                <div
                  {...getItemProps({
                    ...item.itemProps,
                    className: item.className
                  })}
                >
                  {itemContext.useResizeHandle ? (
                    <div {...leftResizeProps} />
                  ) : (
                    ''
                  )}

                  <div
                    className="rct-item-content"
                    style={{ maxHeight: `${itemContext.dimensions.height}` }}
                  >
                    {itemContext.title}
                  </div>

                  {itemContext.useResizeHandle ? (
                    <div {...rightResizeProps} />
                  ) : (
                    ''
                  )}
                </div>
              )
            }}
            items={dataForScheduler}
            defaultTimeStart={moment().add(-3, 'day')}
            defaultTimeEnd={moment().add(3, 'day')}
            onBoundsChange={(canvasTimeStart, canvasTimeEnd) => {
              const startOfDay = DateTime.fromMillis(canvasTimeStart)
                .startOf('day')
                .toFormat('yyyy-MM-dd')
              const endOfDay = DateTime.fromMillis(canvasTimeEnd)
                .endOf('day')
                .toFormat('yyyy-MM-dd')
              setViewingRange({ startOfDay, endOfDay })
              return true
            }}
            onItemClick={(itemId) => {
              if (itemId.startsWith('virtual_')) {
                const virtualItem = dataForScheduler.find((i) => i.id == itemId)
                setIsViewingEntity(virtualItem?.item)
                return
              }
              const originalItemId = itemId.split('-')[0]
              const item = data.find((i) => i.id == originalItemId)
              setIsViewingEntity(item)
            }}
            onTimeChange={(
              visibleTimeStart,
              visibleTimeEnd,
              updateScrollCanvas
            ) => {
              const startOfDay = DateTime.fromMillis(visibleTimeStart)
                .startOf('week')
                .toFormat('yyyy-MM-dd')
              const endOfDay = DateTime.fromMillis(visibleTimeEnd)
                .endOf('week')
                .toFormat('yyyy-MM-dd')

              setViewingRange({ startOfDay, endOfDay })
              updateScrollCanvas(visibleTimeStart, visibleTimeEnd)

              updateFilter({
                'Start_datetime_field.from': startOfDay,
                'Start_datetime_field.to': endOfDay
              })
            }}
          />
        )}
      </ScrollView>
    </View>
  )
}
